/**
 * Shared device signature and device name generation utility
 * Provides consistent device identification across all components
 */

export interface DeviceInfo {
  deviceSignature: string;
  deviceId: string;
  deviceName: string;
  platform: 'ios' | 'android' | 'web';
  deviceType: 'fingerprint' | 'faceid' | 'touchid';
}

/**
 * Detects the platform type
 */
function detectPlatform(): 'ios' | 'android' | 'web' {
  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);
  
  if (isIOS) return 'ios';
  if (isAndroid) return 'android';
  return 'web';
}

/**
 * Detects the biometric type based on platform
 */
function detectBiometricType(platform: 'ios' | 'android' | 'web'): 'fingerprint' | 'faceid' | 'touchid' {
  if (platform === 'ios') {
    // iOS devices typically use Face ID (newer) or Touch ID (older)
    // We'll default to faceid for iOS, but this could be enhanced
    return 'faceid';
  }
  if (platform === 'android') {
    return 'fingerprint';
  }
  // Web platform
  return 'fingerprint';
}

/**
 * Detects the operating system
 */
function detectOS(): string {
  const userAgent = navigator.userAgent;
  
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac')) return 'macOS';
  if (userAgent.includes('Linux')) return 'Linux';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iOS') || /iPad|iPhone|iPod/.test(userAgent)) return 'iOS';
  
  return 'Unknown';
}

/**
 * Detects the browser
 */
function detectBrowser(): string {
  const userAgent = navigator.userAgent;
  
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
  if (userAgent.includes('Edg')) return 'Edge';
  
  return 'Unknown';
}

/**
 * Generates a readable device name for display
 * Examples: "MacBook Pro - Chrome", "iPhone 13 - Safari", "Windows PC - Chrome"
 */
export function generateDeviceName(): string {
  const os = detectOS();
  const browser = detectBrowser();
  const platform = detectPlatform();
  
  // For mobile devices, try to detect device model
  if (platform === 'ios') {
    // Try to detect iPhone/iPad model from user agent
    const userAgent = navigator.userAgent;
    if (userAgent.includes('iPhone')) {
      // Extract iPhone model if available
      const match = userAgent.match(/iPhone\s*(\d+)/);
      if (match) {
        return `iPhone ${match[1]} - ${browser}`;
      }
      return `iPhone - ${browser}`;
    }
    if (userAgent.includes('iPad')) {
      return `iPad - ${browser}`;
    }
    return `iOS Device - ${browser}`;
  }
  
  if (platform === 'android') {
    // Try to extract Android device model
    const userAgent = navigator.userAgent;
    const match = userAgent.match(/Android.*?;\s*([^)]+)\)/);
    if (match && match[1]) {
      const deviceModel = match[1].trim();
      return `${deviceModel} - ${browser}`;
    }
    return `Android Device - ${browser}`;
  }
  
  // For web/desktop, use OS and browser
  if (os === 'macOS') {
    // Try to detect if it's a MacBook
    return `Mac - ${browser}`;
  }
  
  if (os === 'Windows') {
    return `Windows PC - ${browser}`;
  }
  
  if (os === 'Linux') {
    return `Linux - ${browser}`;
  }
  
  return `${os} - ${browser}`;
}

/**
 * Generates a device signature for device matching
 * Uses stable device characteristics to create a consistent signature
 * Avoids timezone and user-agent changes that could break matching
 */
export async function generateDeviceSignature(): Promise<string> {
  // Use stable device characteristics that don't change frequently
  const deviceCharacteristics = [
    navigator.platform || 'unknown',
    `${window.screen.width}x${window.screen.height}`,
    navigator.language,
    navigator.hardwareConcurrency?.toString() || 'unknown',
    (navigator as any).deviceMemory?.toString() || 'unknown',
    // Use OS and browser detection (more stable than full user agent)
    detectOS(),
    detectBrowser(),
  ].join('|');
  
  // Generate hash
  const encoder = new TextEncoder();
  const data = encoder.encode(deviceCharacteristics);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).slice(0, 16).join(''); // Use first 16 chars
  
  // Return format: OS-Browser-hash for readability
  const os = detectOS();
  const browser = detectBrowser();
  return `${os}-${browser}-${hash}`;
}

/**
 * Generates a device ID (shorter identifier)
 */
export async function generateDeviceId(): Promise<string> {
  const deviceCharacteristics = [
    navigator.platform || 'unknown',
    navigator.hardwareConcurrency?.toString() || 'unknown',
    (navigator as any).deviceMemory?.toString() || 'unknown',
    detectOS(),
    detectBrowser(),
  ].join('|');
  
  const encoder = new TextEncoder();
  const data = encoder.encode(deviceCharacteristics);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).slice(0, 8).join(''); // Use first 8 chars
  
  return hash;
}

/**
 * Gets complete device information
 * This is the main function to use for device identification
 */
export async function getDeviceInfo(): Promise<DeviceInfo> {
  const platform = detectPlatform();
  const deviceType = detectBiometricType(platform);
  const deviceSignature = await generateDeviceSignature();
  const deviceId = await generateDeviceId();
  const deviceName = generateDeviceName();
  
  return {
    deviceSignature,
    deviceId,
    deviceName,
    platform,
    deviceType,
  };
}

